import {
  FlightsFilterType,
  FlightsSearchFilterValuesInput,
  FlightsShoppingPwaFlightSearchResultsFlightsSearch,
  FlightsTravelerDetailsInput,
  FlightsTravelerType,
  SelectedValueInput,
  ShoppingSearchCriteriaInput,
  TravelerDetailsInput,
  TravelerDetailsType,
  FlightsSearchPreferencesInput
} from '../__generated__/api/types';

type FlightSearchCriteria = FlightsShoppingPwaFlightSearchResultsFlightsSearch.Variables;

const buildShoppingSearchCriteria = (
  flightsSearchFilterValuesInput: FlightsSearchFilterValuesInput
): SelectedValueInput => {
  if (flightsSearchFilterValuesInput.numOfStopFilterValue) {
    switch (flightsSearchFilterValuesInput.numOfStopFilterValue.stopInfo.numberOfStops) {
      case 2:
        return {
          id: FlightsFilterType.NUM_OF_STOPS,
          value: '2+'
        };
      case 1:
        return {
          id: FlightsFilterType.NUM_OF_STOPS,
          value: '1'
        };
      case 0:
        return {
          id: FlightsFilterType.NUM_OF_STOPS,
          value: '0'
        };
    }
  } else if (flightsSearchFilterValuesInput.arrivalAirportCodeFilterValue) {
    return {
      id: FlightsFilterType.ARRIVAL_AIRPORT,
      value: flightsSearchFilterValuesInput.arrivalAirportCodeFilterValue.airportCode
    };
  } else if (flightsSearchFilterValuesInput.departureAirportCodeFilterValue) {
    return {
      id: FlightsFilterType.DEPARTURE_AIRPORT,
      value: flightsSearchFilterValuesInput.departureAirportCodeFilterValue.airportCode
    };
  } else if (flightsSearchFilterValuesInput.arrivalTimeFilterValue) {
    return {
      id: FlightsFilterType.ARRIVAL_TIME,
      value: flightsSearchFilterValuesInput.arrivalTimeFilterValue.timeOfDay
    };
  } else if (flightsSearchFilterValuesInput.carryOnBagBasedFilterValue) {
    return {
      id: FlightsFilterType.CARRY_ON_BAG,
      value: flightsSearchFilterValuesInput.carryOnBagBasedFilterValue.carryOnBag
    };
  } else if (flightsSearchFilterValuesInput.checkedBagBasedFilterValue) {
    return {
      id: FlightsFilterType.CHECKED_BAG,
      value: flightsSearchFilterValuesInput.checkedBagBasedFilterValue.numOfCheckedBags.toString()
    };
  } else if (flightsSearchFilterValuesInput.creditEligibleFilterValue) {
    return {
      id: FlightsFilterType.CREDIT_ELIGIBLE,
      value: flightsSearchFilterValuesInput.creditEligibleFilterValue.creditEligible
    };
  } else if (flightsSearchFilterValuesInput.departureTimeFilterValue) {
    return {
      id: FlightsFilterType.DEPARTURE_TIME,
      value: flightsSearchFilterValuesInput.departureTimeFilterValue.timeOfDay
    };
  } else if (flightsSearchFilterValuesInput.flexibleChangePolicyBasedFilterValue) {
    return {
      id: FlightsFilterType.FLEXIBLE_CHANGE_POLICIES,
      value: flightsSearchFilterValuesInput.flexibleChangePolicyBasedFilterValue.policyName
    };
  } else if (flightsSearchFilterValuesInput.noCancelFeeBasedFilterValue) {
    return {
      id: FlightsFilterType.NO_CANCEL_FEE,
      value: flightsSearchFilterValuesInput.noCancelFeeBasedFilterValue.noCancelFee
    };
  } else if (flightsSearchFilterValuesInput.seatChoiceBasedFilterValue) {
    return {
      id: FlightsFilterType.SEAT_CHOICE,
      value: flightsSearchFilterValuesInput.seatChoiceBasedFilterValue.seatChoice
    };
  } else if (flightsSearchFilterValuesInput.durationFilterValue) {
    return {
      id: FlightsFilterType.DURATION,
      value: flightsSearchFilterValuesInput.durationFilterValue.maxDuration.toString()
    };
  } else if (flightsSearchFilterValuesInput.preferredAirlineFilterValue) {
    return {
      id: FlightsFilterType.PREFERRED_AIRLINE,
      value: flightsSearchFilterValuesInput.preferredAirlineFilterValue.carrierCode
    };
  }
};

export const flightSearchSecondaryShoppingCriteriaMapper = (
  secondaryCriteria: FlightSearchCriteria['searchFilterValuesList']
): ShoppingSearchCriteriaInput => {
  const filters = [];
  const flightsSearchFilterValuesInputs = Array.isArray(secondaryCriteria) ? secondaryCriteria : [secondaryCriteria];

  flightsSearchFilterValuesInputs.forEach(item => {
    filters.push(buildShoppingSearchCriteria(item));
  });

  return {
    selections: filters
  };
};

const travelerTypeMapper: Record<FlightsTravelerType, TravelerDetailsType> = {
  [FlightsTravelerType.ADULT]: TravelerDetailsType.ADULT,
  [FlightsTravelerType.CHILD]: TravelerDetailsType.CHILD,
  [FlightsTravelerType.INFANT_IN_LAP]: TravelerDetailsType.INFANT_IN_LAP,
  [FlightsTravelerType.INFANT_IN_SEAT]: TravelerDetailsType.INFANT_IN_SEAT
};

const buildTravelerDetailsInput = (travelers: FlightsTravelerDetailsInput): TravelerDetailsInput[] => {
  const flightsTravelerDetailsInputs = [];

  for (let index = 0; index < travelers.count; index++) {
    flightsTravelerDetailsInputs.push({
      age: travelers.ages?.[index],
      type: travelerTypeMapper[travelers.travelerType]
    });
  }

  return flightsTravelerDetailsInputs;
};

export const flightSearchTravelersDetailsMapper = (
  travelerDetails: FlightSearchCriteria['travelerDetails']
): TravelerDetailsInput[] => {
  const flightsTravelerDetailsInputs = Array.isArray(travelerDetails) ? travelerDetails : [travelerDetails];

  return flightsTravelerDetailsInputs.flatMap(item => buildTravelerDetailsInput(item));
};

export const flightSearchPreferencesMapper = (
  searchPreferences: FlightsSearchPreferencesInput
): FlightsSearchPreferencesInput => ({
  cabinClass: searchPreferences.cabinClass,
  advancedFilters: searchPreferences.advancedFilters ?? [],
  airline: searchPreferences.airline || '*'
});
